// lib/bridgeApi.js
//
// Typed wrapper around the LIVE mainnet bridge backend surface. As of
// 2026-07-14 this is a SINGLE public, read-only endpoint:
//
//   GET /bridge/routes  -> { routes: [...], excluded: [...], generatedAtMs }
//
// There is no POST /bridge/start any more — the OLD testnet flow (server
// creates an "intent", the frontend polls server-tracked state) has been
// retired for this app. The live path is fully non-custodial: the customer's
// own wallet signs approve() + requestSwap() directly against the mainnet
// BridgeSenderUpgradeable (see lib/bridgeCalldata.js), and delivery is
// observed TRUSTLESSLY by polling the destination BridgeReceiverUpgradeable's
// public `fulfilled(swapId)` mapping (see hooks/useBridgeDelivery.js) — no
// server-side intent store to keep in sync with the chain.
//
// The BRIDGE_STATES / stateLabel / isStuckState helpers below describe the
// CLIENT-DERIVED progress state machine (composed from wallet-tx state +
// on-chain reads in BridgeStatusPage), not a server-tracked one. The state
// names are kept identical to the pre-2026-07-14 server contract so
// StateTimeline / ProgressBar need no changes.

import { adminFetch } from '../_shared/wallet-app/useAdminFetch';

export const BRIDGE_STATES = [
  'intent_created',
  'burn_submitted',
  'burn_confirmed',
  'relay_submitted',
  'mint_confirmed',
];

export const STUCK_PREFIX = 'stuck_';

export function isTerminalState(state) {
  return state === 'mint_confirmed' || (state || '').startsWith(STUCK_PREFIX);
}

export function isStuckState(state) {
  return (state || '').startsWith(STUCK_PREFIX);
}

// Compact step labels for the timeline.
export const STATE_LABELS = {
  intent_created: 'Intent created',
  burn_submitted: 'Burn submitted',
  burn_confirmed: 'Burn confirmed',
  relay_submitted: 'Awaiting relay',
  mint_confirmed: 'Mint confirmed',
};

export const stateLabel = (state) => {
  if (STATE_LABELS[state]) return STATE_LABELS[state];
  if (isStuckState(state)) {
    const step = state.slice(STUCK_PREFIX.length).replace(/_/g, ' ');
    return `Stuck: ${step}`;
  }
  return state || 'unknown';
};

// 0..1 progress fraction for the linear progress bar.
export function stateProgress(state) {
  const idx = BRIDGE_STATES.indexOf(state);
  if (idx < 0) {
    if (isStuckState(state)) {
      const stepName = state.slice(STUCK_PREFIX.length);
      const guess = BRIDGE_STATES.findIndex((s) => s.startsWith(stepName));
      if (guess >= 0) return (guess + 1) / BRIDGE_STATES.length;
      return 0.5;
    }
    return 0;
  }
  return (idx + 1) / BRIDGE_STATES.length;
}

// ── HTTP wrapper ─────────────────────────────────────────────────────────────

/**
 * GET /bridge/routes — the single source of truth for which (src, dst,
 * kStable) triples are live right now. Server-driven: every entry has
 * already been checked against paused()/supportedKStable() on-chain, so a
 * route appearing here is safe to render as bridgeable.
 */
export async function fetchBridgeRoutes() {
  return adminFetch('/bridge/routes');
}
